import { currencyConstants } from './currencyConstants';
const currencies = currencyConstants.currencies;
const currencySymbols = currencyConstants.symbols;
const abbreviations = currencyConstants.abbreviations;

/**
 * A more forgiving parseFloat
 * This is useful when consuming user-input
 * @param {number|string} number Either a formatted string (3,000) or integer (3000)
 * @returns {Number}
 */
export const dibsParseFloat = (number: number | string): number => {
    return parseFloat(String(number).replace(/[^\d\.e-]/gi, '')) || 0;
};

/**
 * Round to the given precision
 * @param {number|string} number @see Number.parseFloat#number
 * @param {number} digits
 * @returns {number}
 */
export const dibsRound = (number: number | string, digits: number): number => {
    const powed = Math.pow(10, digits);
    return Math.round(dibsParseFloat(number) * powed) / powed;
};

/**
 * Similar to PHP's number_format
 * @param number
 * @param {number} significantDigits The decimal place to round and fix a number to, Defaults to 0
 * @param {String} thousandsSeparator Defaults to ,
 * @param {String} decimalMark Defaults to .
 */
export const dibsFormat = (
    number: number,
    significantDigits = 0,
    thousandsSeparator = ',',
    decimalMark = '.'
): string => {
    let suffix = '';
    const isNegative = number < 0;
    let bits;
    let i;

    if (isNegative) {
        number = number * -1;
    }

    significantDigits = significantDigits > -1 ? significantDigits : 0;
    let newNumber = dibsRound(number, significantDigits).toFixed(significantDigits);
    if (thousandsSeparator && newNumber.match(/^\d{4}/)) {
        if (significantDigits) {
            suffix = newNumber.substr(newNumber.indexOf('.'));
            newNumber = newNumber.split('.')[0];
        }
        bits = newNumber.split('');
        i = bits.length - 3;
        while (i > 0) {
            bits.splice(i, 0, thousandsSeparator);
            i -= 3;
        }
        newNumber = bits.concat(suffix).join('');
    }
    if (decimalMark !== '.' && significantDigits) {
        newNumber = newNumber.replace(/\.(\d+)$/, decimalMark + '$1');
    }

    if (isNegative) {
        newNumber = '-' + newNumber;
    }

    return newNumber;
};

/**
 *
 * @param {number} number The number to format
 * @param {object} [options] Formatting options
 * @param {boolean} [options.showDecimal] Show a decimal or not. Defaults to true
 * @param {string} [options.thousandsSeparator] What separates the thousands? Defaults to comma ","
 * @param {string} [options.decimalMark] What separates decimals? Defaults to period "."
 * @param {string} [options.abbreviation] Wheter to include an abbreviation or not
 * @param {boolean} [options.includeCurrencySymbol] Wheter to include a currency symbol or not
 * @param {string} [options.currencySymbol] Which currency symbol to use. Defaults to USD "$"
 * @returns {object}
 */

type LocalizedCashFormatOptionsType = {
    showDecimal?: boolean;
    thousandsSeparator?: string;
    decimalMark?: string;
    abbreviation?: string;
    includeCurrencySymbol?: boolean;
    currencySymbol?: string;
    formatNegativeAmount?: boolean;
};

type localizedCashFormattedObjType = {
    sign: string;
    currencySymbol?: string;
    number?: string;
    abbreviation?: string;
};

export const localizedCashFormatObj = (
    number: number,
    options: LocalizedCashFormatOptionsType
): localizedCashFormattedObjType => {
    options = Object.assign(
        {
            includeCurrencySymbol: true,
        },
        options
    );
    const decimalsToShow = options.showDecimal === false ? 0 : 2;
    const thousandsSeparator = options.thousandsSeparator || ',';
    const decimalMark = options.decimalMark || '.';
    let currencySymbol = options.currencySymbol || '$';
    const abbreviation = options.abbreviation || '';
    let sign = '';

    let newNumber = dibsFormat(number, decimalsToShow, thousandsSeparator, decimalMark);
    currencySymbol = options.includeCurrencySymbol ? currencySymbol : '';

    // if it's negative we need to put the negative sign in front
    if (newNumber.indexOf('-') !== -1) {
        sign = '-';
        newNumber = newNumber.replace('-', '');
    }

    return {
        sign: sign,
        currencySymbol: currencySymbol,
        number: newNumber,
        abbreviation: abbreviation,
    };
};

/**
 *
 * @param {number} number The number to format
 * @param {object} [options] Formatting options
 * @param {boolean} [options.showDecimal] Show a decimal or not. Defaults to true
 * @param {string} [options.thousandsSeparator] What separates the thousands? Defaults to comma ","
 * @param {string} [options.decimalMark] What separates decimals? Defaults to period "."
 * @param {string} [options.abbreviation] Wheter to include an abbreviation or not
 * @param {boolean} [options.includeCurrencySymbol] Wheter to include a currency symbol or not
 * @param {string} [options.currencySymbol] Which currency symbol to use. Defaults to USD "$"
 * @returns {string}
 */
export const localizedCashFormat = (
    number: number,
    options: LocalizedCashFormatOptionsType
): string => {
    const localizedObject = localizedCashFormatObj(number, options);
    const localizedNumber = localizedObject.number;
    const currencySymbol = localizedObject.currencySymbol;
    let abbreviation = localizedObject.abbreviation;
    let sign = localizedObject.sign;

    // if it's negative we need to put the negative sign in front
    if (sign) {
        sign = sign + ' ';
    }

    if (abbreviation && abbreviation.length) {
        abbreviation = ' ' + abbreviation;
    }

    if (options.formatNegativeAmount && sign) {
        return '(' + currencySymbol + localizedNumber + abbreviation + ')';
    }

    return sign + currencySymbol + localizedNumber + abbreviation;
};

/**
 * Converts a string containing numbers to an integer
 * @param  {string} amount
 * @return {number}
 */
export const currencyToInteger = (amount: string): number => {
    // Match all numbers and - sign
    const regexp = /-?[0-9]*/g;
    if (typeof amount === 'number') {
        return amount;
    } else if (amount && typeof amount === 'string') {
        const matchedAmount = amount.match(regexp);
        if (matchedAmount && matchedAmount.length) {
            return parseInt(matchedAmount.join(''), 10);
        }
    }
    return NaN;
};

/**
 * Used internaly for intelligentFormat
 * @param  {number} The number to format
 * @param  {string}
 * @param  {object} passedOptions
 * @return {object}
 */
export const getFormatOptions = (
    amount: number,
    currency: string | undefined,
    passedOptions: LocalizedCashFormatOptionsType
): LocalizedCashFormatOptionsType => {
    const num = typeof amount === 'number' ? amount : parseFloat(amount);
    const currencyUppercase = currency ? (currency + '').toUpperCase() : 'USD';
    const options = {
        // Only if there are decimals do we show them
        showDecimal: Math.ceil(num) !== Math.floor(num),
        thousandsSeparator: ',',
        decimalMark: '.',
        currencySymbol: currencySymbols.USD,
        abbreviation: abbreviations[currencyUppercase],
        formatNegativeAmount: false,
        includeCurrencySymbol: true,
    };

    switch (currencyUppercase) {
        case currencies.CURRENCY_GBP:
        case currencySymbols.GBP:
            options.currencySymbol = currencySymbols.GBP;
            break;
        case currencies.CURRENCY_CHF:
        case currencySymbols.CHF:
            options.includeCurrencySymbol = false;
            break;
        case currencies.CURRENCY_EUR:
        case currencySymbols.EUR:
            options.thousandsSeparator = '.';
            options.decimalMark = ',';
            options.currencySymbol = currencySymbols.EUR;
            break;
    }

    return Object.assign(options, passedOptions);
};

/**
 * @param  {string} currency currency abbreviation
 * @param  {string} size sm || md
 * @return {string} image url
 */

export const getCurrencyFlags = (currency: string, size?: string): string => {
    return size === 'md'
        ? currencyConstants.flags.md[currency]
        : currencyConstants.flags.sm[currency];
};
